<template>
  <div ref="filterBoxRef">
    <DolphinSelector
      v-if="hasFavoriteFilteringRules"
      :class="$style.component__selector"
      :options="favoriteFilteringRuleOptions"
      placeholder="---------------"
      v-model="model"
      @change="$emit('select')"
      ellipsis
      :style="filterStyle"
    />
  </div>
  <div :class="[$style.component__action, $style.action]">
    <FlatButton variant="primary" size="small" @click="openSavingModal">
      {{ savingButtonText }}
    </FlatButton>
    <FlatButton
      v-if="hasFavoriteFilteringRules"
      variant="secondary"
      size="small"
      :disabled="!isFavoriteFilteringRuleSelected"
      @click="openUpdatingModal"
    >
      更新
    </FlatButton>
    <FlatButton
      v-if="hasFavoriteFilteringRules"
      :class="$style.action__button"
      variant="caution"
      size="small"
      :disabled="!isFavoriteFilteringRuleSelected"
      @click="openRemovingModal"
    >
      削除
    </FlatButton>

    <SavingModal>
      <SaveKarteUserFilteringRuleComfirmation
        @close="closeSavingModal"
        @submit="onSave"
      />
    </SavingModal>
    <RemovingModal>
      <RemoveKarteUserFilteringRuleComfirmation
        :selectedFilteringRuleLabel="selectedFavoriteFilteringRuleLabel"
        @close="closeRemovingModal"
        @submit="onRemove"
      />
    </RemovingModal>
    <UpdatingModal>
      <UpdateKarteUserFilteringRuleComfirmation
        :selectedFilteringRuleLabel="selectedFavoriteFilteringRuleLabel"
        @close="closeUpdatingModal"
        @submit="onUpdate"
      />
    </UpdatingModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import type { DeepReadonly } from 'vue'
import { useElementBounding } from '@vueuse/core'

import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import SaveKarteUserFilteringRuleComfirmation from './SaveKarteUserFilteringRuleComfirmation.vue'
import RemoveKarteUserFilteringRuleComfirmation from './RemoveKarteUserFilteringRuleComfirmation.vue'
import UpdateKarteUserFilteringRuleComfirmation from './UpdateKarteUserFilteringRuleComfirmation.vue'

import { useModal } from '@/hooks/useModal'

import type { Option } from '@/types/app'

import type { FavoriteKarteUserFilteringRule } from '../hooks/useKarteUserFilteringRule'
import FlatButton from '@/components/atoms/FlatButton.vue'

// workaround
const filterBoxRef = ref<HTMLElement | null>(null)
const filterBoxBounding = useElementBounding(filterBoxRef)
const filterStyle = computed(() => {
  const boxWidth = filterBoxBounding.width.value
  return boxWidth > 0 ? { width: `${boxWidth.toFixed(0)}px` } : {}
})

onMounted(() => {
  filterBoxBounding.update()
})

const props = withDefaults(
  defineProps<{
    modelValue?: number // selectedFavoriteFilteringRuleIndex
    rules?: DeepReadonly<FavoriteKarteUserFilteringRule[]>
  }>(),
  { rules: () => [] }
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: Maybe<number>): void
  (event: 'update', value: string): void
  (event: 'remove'): void
  (event: 'save', value: string): void
  (event: 'select'): void
}>()

const model = computed({
  get: () => props.modelValue,
  set: (value: Maybe<number>): void => {
    emit('update:modelValue', value)
  },
})

const hasFavoriteFilteringRules = computed(
  (): boolean => props.rules.length > 0
)

const isFavoriteFilteringRuleSelected = computed(
  (): boolean => props.modelValue != null
)

const favoriteFilteringRuleOptions = computed<Option<number | null>[]>(() => [
  {
    value: null,
    text: '-----',
  },
  ...props.rules.map((item, index) => ({
    value: index,
    text: item.label,
  })),
])

const selectedFavoriteFilteringRuleLabel = computed(() =>
  props.modelValue != null ? props.rules[props.modelValue]?.label : ''
)

const savingButtonText = computed(() =>
  hasFavoriteFilteringRules.value ? '新規保存' : 'この検索条件を保存'
)

const {
  ControlledModal: SavingModal,
  openModal: openSavingModal,
  closeModal: closeSavingModal,
} = useModal()

const {
  ControlledModal: RemovingModal,
  openModal: openRemovingModal,
  closeModal: closeRemovingModal,
} = useModal()

const {
  ControlledModal: UpdatingModal,
  openModal: openUpdatingModal,
  closeModal: closeUpdatingModal,
} = useModal()

const onSave = (label: string) => emit('save', label)
const onUpdate = (updatedLabel: string) => emit('update', updatedLabel)
const onRemove = () => emit('remove')
</script>

<style lang="scss" module>
.list {
  display: grid;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;

  &__contents {
    padding: 12px 0;
    overflow-y: scroll;

    &:nth-child(n + 2) {
      border-top: 1px solid $color-border-low-emphasis;
    }
  }
}

.component {
  $header-padding: 24px;

  display: flex;
  justify-content: space-between;
  padding: 24px;

  &--editable {
    display: flex;
    flex-direction: column;
  }

  &__action {
    display: flex;
  }

  &__selector {
    width: 100%;
    margin-top: 16px;
    font-size: 16px;
  }
}

.action {
  display: flex;
  height: 40px;
  justify-content: space-between;
  gap: 8px;

  &:nth-child(n + 2) {
    width: 100%;
    margin-top: 16px;
  }

  &__button {
    flex: 1;
  }
}
</style>
